import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query'

import type { CustomAssessmentInput, ISFPExtractedData, RetrofitName } from '@/shared/types'

import { isfpDataApi, type CombinedDataPayload } from './isfp-data.api'

/**
 * Query key factory for ISFP data queries
 */
export const isfpDataKeys = {
  all: ['isfp-data'] as const,
  isfpData: (buildingUuid: string) => [...isfpDataKeys.all, 'isfp', buildingUuid] as const,
  customAssessmentInput: (buildingUuid: string) =>
    [...isfpDataKeys.all, 'custom-assessment-input', buildingUuid] as const,
}

interface SubmitISFPDataParams {
  buildingUuid: string
  data: ISFPExtractedData
}

interface SubmitCustomAssessmentInputParams {
  buildingUuid: string
  data: CustomAssessmentInput
}

interface UpdateHiddenRetrofitsParams {
  buildingUuid: string
  hiddenRetrofits: RetrofitName[]
}

/**
 * Hook to fetch existing ISFP data for a building
 * @param buildingUuid - The UUID of the building
 * @param options - Optional React Query options
 */
export const useISFPData = (
  buildingUuid: string | undefined,
  options?: Omit<UseQueryOptions<ISFPExtractedData, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<ISFPExtractedData, Error>({
    queryKey: isfpDataKeys.isfpData(buildingUuid!),
    queryFn: () => isfpDataApi.getISFPData(buildingUuid!),
    ...options,
    enabled: !!buildingUuid && options?.enabled !== false,
  })
}

/**
 * Hook for submitting confirmed ISFP data to eb-hub backend
 */
export const useSubmitISFPData = (
  options?: UseMutationOptions<void, Error, SubmitISFPDataParams>
) => {
  return useMutation({
    mutationFn: ({ buildingUuid, data }: SubmitISFPDataParams) =>
      isfpDataApi.submitISFPData(buildingUuid, data),
    ...options,
  })
}

/**
 * Hook to fetch custom assessment input (optional ISFP fields default values)
 * @param buildingUuid - The UUID of the building
 * @param options - Optional React Query options
 */
export const useCustomAssessmentInput = (
  buildingUuid: string | undefined,
  options?: Omit<UseQueryOptions<CustomAssessmentInput, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<CustomAssessmentInput, Error>({
    queryKey: isfpDataKeys.customAssessmentInput(buildingUuid!),
    queryFn: () => isfpDataApi.getCustomAssessmentInput(buildingUuid!),
    ...options,
    enabled: !!buildingUuid && options?.enabled !== false,
  })
}

/**
 * Hook for submitting custom assessment input to eb-hub backend
 */
export const useSubmitCustomAssessmentInput = (
  options?: UseMutationOptions<void, Error, SubmitCustomAssessmentInputParams>
) => {
  return useMutation({
    mutationFn: ({ buildingUuid, data }: SubmitCustomAssessmentInputParams) =>
      isfpDataApi.submitCustomAssessmentInput(buildingUuid, data),
    ...options,
  })
}

/**
 * Hook for submitting combined ISFP data and custom assessment input to eb-hub backend
 */
export const useSubmitCombinedData = (
  options?: UseMutationOptions<void, Error, CombinedDataPayload>
) => {
  return useMutation({
    mutationFn: (data: CombinedDataPayload) => isfpDataApi.submitCombinedData(data),
    ...options,
  })
}

/**
 * Hook for updating hidden retrofits for a building
 */
export const useUpdateHiddenRetrofits = (
  options?: UseMutationOptions<void, Error, UpdateHiddenRetrofitsParams>
) => {
  return useMutation({
    mutationFn: ({ buildingUuid, hiddenRetrofits }: UpdateHiddenRetrofitsParams) =>
      isfpDataApi.updateHiddenRetrofits(buildingUuid, hiddenRetrofits),
    ...options,
  })
}
